import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id: number;
  name: string;
  username: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  sessionToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [sessionError, setSessionError] = useState(false);
  const [retry, setRetry] = useState(0);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      setIsLoading(true); setSessionError(false);
      const storedToken = localStorage.getItem('sessionToken');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Validate session with backend
          const response = await fetch('/api/current-user', {
            headers: {
              'X-Session-Token': storedToken || ''
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
            setSessionToken(storedToken);
          } else if ([401, 403].includes(response.status)) {
            // Session invalid, clear storage
            localStorage.removeItem('sessionToken');
            localStorage.removeItem('user');
          } else { setSessionError(true); }
        } catch (error) {
          console.error('Error validating session:', error);
          setSessionError(true);
        }
      }

      setIsLoading(false);
    };

    checkSession();
  }, [retry]);

  const login = (userData: User, token: string) => {
    setUser(userData);
    setSessionToken(token);
    localStorage.setItem('sessionToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = async () => {
    if (sessionToken) {
      try {
        await fetch('/api/logout', {
          method: 'POST',
          headers: {
            'X-Session-Token': sessionToken || ''
          }
        });
      } catch (error) {
        console.error('Error logging out:', error);
      }
    }

    setUser(null);
    setSessionToken(null);
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('user');
  };

  const value: AuthContextType = {
    user,
    sessionToken,
    isAuthenticated: !!user && !!sessionToken,
    isLoading,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{sessionError ? <div className="login-container"><div className="login-box"><h1>Connection interrupted</h1><p role="alert">We couldn’t verify your session. Reconnect and retry to continue.</p><button onClick={() => setRetry(n => n + 1)}>Retry connection</button></div></div> : children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
